import { useState, useEffect, type ReactNode } from 'react';
import {
  Bar,
  BarChart,
  XAxis,
  YAxis,
  Cell,
} from 'recharts';
import { ExternalLink, Check, X, ChevronDown, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { ChartConfig, ChartContainer } from '@/components/ui/chart';
import GoNoGoBadge from './GoNoGoBadge';
import { EntryPathChip, ObjectiveChip } from './Chip';
import { useDisplayOptions } from '@/lib/display-options';
import type { GrantProgram, ProgramScore, ViewMode } from '@/types/roadmap.types';

interface ProgramCardProps {
  program: GrantProgram;
  compact?: boolean;
  className?: string;
  /** When `presentation`, description starts collapsed and resets collapsed when mode changes */
  viewMode?: ViewMode;
}

const SCORE_DIMENSIONS: { key: keyof Omit<ProgramScore, 'composite'>; label: string }[] = [
  { key: 'mission_alignment', label: 'Mission' },
  { key: 'eligibility_gate', label: 'Eligibility' },
  { key: 'readiness', label: 'Readiness' },
  { key: 'funding_fit', label: 'Funding' },
  { key: 'strategic_value', label: 'Strategic' },
  { key: 'application_burden', label: 'Burden' },
];

const chartConfig: ChartConfig = {
  score: {
    label: 'Score',
    color: 'hsl(var(--primary))',
  },
};

/** All-caps section headings — stronger than body for scanability */
const sectionLabelClass =
  'text-[11px] print:text-[8pt] uppercase tracking-wider text-foreground font-semibold';

function barFill(value: number): string {
  if (value >= 4) return 'hsl(var(--primary))';
  if (value >= 3) return 'hsl(var(--primary) / 0.7)';
  return 'hsl(var(--primary) / 0.4)';
}

const CHART_MARGIN = { top: 2, right: 6, bottom: 4, left: 0 } as const;
const PRINT_CHART_W = 280;
const PRINT_CHART_H = 164;

function ScoreBarChart({
  data,
  width,
  height,
}: {
  data: { dimension: string; score: number }[];
  width?: number;
  height?: number;
}) {
  return (
    <BarChart
      data={data}
      layout="vertical"
      margin={CHART_MARGIN}
      barCategoryGap="12%"
      {...(width != null && height != null ? { width, height } : {})}
    >
      <XAxis
        type="number"
        domain={[0, 5]}
        ticks={[1, 2, 3, 4, 5]}
        tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }}
        axisLine={false}
        tickLine={false}
        height={20}
      />
      <YAxis
        type="category"
        dataKey="dimension"
        tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))', textAnchor: 'end' }}
        axisLine={false}
        tickLine={false}
        width={76}
        interval={0}
        tickMargin={2}
      />
      <Bar dataKey="score" radius={[0, 3, 3, 0]} maxBarSize={12}>
        {data.map((entry, i) => (
          <Cell key={i} fill={barFill(entry.score)} />
        ))}
      </Bar>
    </BarChart>
  );
}

function ScoreBars({ score }: { score: ProgramScore }) {
  const data = SCORE_DIMENSIONS.map((d) => ({
    dimension: d.label,
    score: score[d.key],
  }));

  return (
    <>
      {/* Screen: uses ResponsiveContainer via ChartContainer (needs JS resize measurement) */}
      <ChartContainer
        config={chartConfig}
        className={cn(
          'w-full min-h-[168px] h-[168px] !aspect-auto pl-0 pr-0 print:!hidden',
          'flex !flex-row !items-start !justify-start overflow-visible',
          '[&_.recharts-responsive-container]:!w-full [&_.recharts-responsive-container]:!h-full [&_.recharts-responsive-container]:min-w-0 [&_.recharts-responsive-container]:flex-1',
          '[&_.recharts-responsive-container]:!max-w-none [&_.recharts-wrapper]:!mx-0 [&_.recharts-wrapper]:!max-w-none',
          '[&_.recharts-surface]:outline-none',
        )}
      >
        <ScoreBarChart data={data} />
      </ChartContainer>

      {/* Print: fixed-size BarChart — bypasses ResponsiveContainer entirely */}
      <div className="hidden print:!block" style={{ width: PRINT_CHART_W, height: PRINT_CHART_H }}>
        <ScoreBarChart data={data} width={PRINT_CHART_W} height={PRINT_CHART_H} />
      </div>
    </>
  );
}

function EntryFlag({ value, label }: { value: boolean; label: string }) {
  return (
    <span className="flex items-center gap-1.5 text-muted-foreground">
      {value ? (
        <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
      ) : (
        <X className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />
      )}
      {label}
    </span>
  );
}

function RequirementCard({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="program-card__req-card rounded-lg border border-border/50 bg-muted/10 p-3 sm:p-4 min-w-0 flex flex-col h-full">
      <p className={cn(sectionLabelClass, 'mb-2 print:mb-1.5')}>
        {title}
      </p>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

export default function ProgramCard({
  program: p,
  compact = false,
  className,
  viewMode,
}: ProgramCardProps) {
  const { showObjective, showEntryPath } = useDisplayOptions();
  const [descriptionOpen, setDescriptionOpen] = useState(
    () => viewMode !== 'presentation',
  );

  useEffect(() => {
    if (viewMode === 'presentation') setDescriptionOpen(false);
  }, [viewMode]);

  return (
    <div
      className={cn(
        'program-card rounded-xl border border-border/60 bg-white transition-shadow hover:shadow-md',
        compact ? 'p-4' : 'p-5 sm:p-6',
        'print:p-4 print:rounded-none print:border print:shadow-none',
        className,
      )}
    >
      {/* Header: title · difficulty note (when present) + Go/No-Go */}
      <div className="program-card__header flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4 mb-3 print:mb-2">
        <div className="min-w-0 flex-1">
          <h3 className={cn(
            'font-semibold text-foreground print:text-[11pt]',
            compact ? 'text-sm' : 'text-base',
          )}>
            {p.program_name}
          </h3>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground mt-0.5 print:text-[8pt]">
            <span>{p.grantor}</span>
            {!compact && <span aria-hidden>&middot;</span>}
            {!compact && <span>{p.country}</span>}
            {showObjective && p.investment_layer && p.investment_layer !== 'general' && (
              <>
                <span aria-hidden>&middot;</span>
                <ObjectiveChip value={p.investment_layer} />
              </>
            )}
            {showEntryPath && p.entry_path && (
              <>
                <span aria-hidden>&middot;</span>
                <EntryPathChip value={p.entry_path} />
              </>
            )}
            {p.source_url && (
              <a
                href={p.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 ml-1 text-primary hover:underline print:hidden"
              >
                View Program <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
        <div className="shrink-0 flex flex-row flex-wrap items-start justify-end gap-2 sm:gap-3">
          {!compact && p.difficulty_rationale && (
            <p className="program-card__difficulty-rationale text-[10px] print:text-[7pt] text-muted-foreground leading-snug text-right sm:max-w-[min(280px,40vw)] print:max-w-[220px] line-clamp-3">
              {p.difficulty_rationale}
            </p>
          )}
          <GoNoGoBadge value={p.go_no_go} />
        </div>
      </div>

      {compact && (
        <>
          <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
            {p.fit_explanation}
          </p>
          <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
            <span><strong className="text-foreground">Award:</strong> {p.award_amount.display}</span>
            <span><strong className="text-foreground">Deadline:</strong>{' '}
              <span className={p.deadline.type === 'rolling' ? 'text-blue-600' : ''}>
                {p.deadline.display}
              </span>
            </span>
          </div>
        </>
      )}

      {!compact && (
        <>
          {/* ── PRINT PAGE 1: Overview ── */}
          {/* Why This Fits */}
          <div className="program-card__fit rounded-lg bg-muted/30 border border-border/30 p-4 mb-4 print:p-3 print:mb-3">
            <p className={cn(sectionLabelClass, 'mb-1')}>
              Why This Fits
            </p>
            <p className="text-sm print:text-[9pt] text-foreground/80 leading-relaxed print:leading-snug">
              {p.fit_explanation}
            </p>
          </div>

          {/* Primary Blocker */}
          {p.primary_blocker && (
            <div className="program-card__blocker flex items-start gap-2 rounded-lg border border-amber-200/70 bg-amber-50/50 px-3 py-2.5 mb-4 print:mb-3 print:py-2 print:px-2.5">
              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className={cn(sectionLabelClass, 'mb-0.5 text-amber-700')}>
                  Primary Blocker
                </p>
                <p className="text-sm print:text-[8pt] text-amber-900/80 leading-snug">
                  {p.primary_blocker}
                </p>
              </div>
            </div>
          )}

          {/* Eligibility + Scoring Rubric */}
          <div className="program-card__body flex flex-col gap-4 print:gap-3">
            <div
              className={cn(
                'program-card__summary-row grid items-start gap-4 print:gap-3',
                p.score
                  ? 'grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(268px,auto)]'
                  : 'grid-cols-1',
              )}
            >
              <div className="program-card__eligibility-card program-card__eligibility min-w-0 rounded-lg border border-border/60 bg-muted/20 p-3 sm:p-3.5">
                <p className={cn(sectionLabelClass, 'mb-2 print:mb-1.5')}>
                  Eligibility
                </p>
                <ul className="space-y-1 print:space-y-0.5">
                  {p.eligibility.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-sm print:text-[8pt] text-muted-foreground"
                    >
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-primary/40 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {p.score && (
                <div className="program-card__chart w-full min-w-[268px] max-w-[300px] justify-self-start self-start text-left overflow-visible">
                  <p className={cn(sectionLabelClass, 'mb-1.5 print:mb-1 text-left')}>
                    Scoring Rubric
                  </p>
                  <ScoreBars score={p.score} />
                  <p className="text-[11px] print:text-[8pt] text-muted-foreground text-left tabular-nums -mt-0.5">
                    Composite:{' '}
                    <span className="font-medium text-foreground">{p.score.composite}</span> / 5.0
                  </p>
                </div>
              )}
            </div>

            {/* Key Facts bar */}
            <div className="program-card__keyfacts rounded-lg border border-border/60 bg-muted/20 p-3 sm:p-3.5 w-full">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-x-4 gap-y-3 text-sm print:text-[9pt]">
                <div className="min-w-0">
                  <p className={cn(sectionLabelClass, 'mb-0.5')}>
                    Award
                  </p>
                  <p className="font-medium leading-snug">{p.award_amount.display}</p>
                </div>
                <div className="min-w-0">
                  <p className={cn(sectionLabelClass, 'mb-0.5')}>
                    Deadline
                  </p>
                  <p
                    className={cn(
                      'font-medium leading-snug',
                      p.deadline.type === 'rolling' && 'text-blue-600',
                    )}
                  >
                    {p.deadline.display}
                  </p>
                </div>
                <div className="min-w-0">
                  <p className={cn(sectionLabelClass, 'mb-0.5')}>
                    Type
                  </p>
                  <p className="font-medium capitalize leading-snug">
                    {p.funding_type.replace('_', ' ')}
                  </p>
                </div>
                <div className="min-w-0">
                  <p className={cn(sectionLabelClass, 'mb-0.5')}>
                    Funding Level
                  </p>
                  <p className="font-medium leading-snug">
                    {p.funding_percentage != null
                      ? `Up to ${Math.round(p.funding_percentage * 100)}%`
                      : p.funding_level_display ?? '—'}
                  </p>
                </div>
                <div className="min-w-0">
                  <p className={cn(sectionLabelClass, 'mb-0.5')}>
                    Cost Sharing
                  </p>
                  <p className="font-medium leading-snug">
                    {p.cost_sharing.required
                      ? p.cost_sharing.ratio ?? 'Required'
                      : 'Not required'}
                  </p>
                  {p.cost_sharing.details && (
                    <p className="text-xs text-muted-foreground mt-1 print:text-[7pt] leading-snug">
                      {p.cost_sharing.details}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── PRINT PAGE 2: Requirements & Description ── */}
          <div className="program-card__page2 print:break-before-page">
            {/* Repeated mini-header so the reader knows which program this belongs to */}
            <div className="hidden print:flex print:items-center print:gap-3 print:mb-3">
              <h4 className="font-semibold text-foreground print:text-[10pt]">
                {p.program_name}
              </h4>
              <GoNoGoBadge value={p.go_no_go} />
            </div>

            {/* 2×2 grid: Eligible | Application / Not eligible | Entry */}
            <div className="program-card__details mt-4 print:mt-0">
              <div className="program-card__requirements-grid grid grid-cols-1 md:grid-cols-2 gap-4 print:grid-cols-2 print:gap-3">
                <RequirementCard title="Eligible Costs">
                  {Array.isArray(p.eligible_expenses) && p.eligible_expenses.length > 0 ? (
                    <p className="text-sm print:text-[8pt] text-muted-foreground leading-relaxed print:leading-snug">
                      {p.eligible_expenses.join(' · ')}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground/70">—</p>
                  )}
                </RequirementCard>

                <RequirementCard title="Application Requirements">
                  {Array.isArray(p.application_components) && p.application_components.length > 0 ? (
                    <p className="text-sm print:text-[8pt] text-muted-foreground leading-relaxed print:leading-snug">
                      {p.application_components.join(' · ')}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground/70">—</p>
                  )}
                </RequirementCard>

                <RequirementCard title="Not Eligible for Funding">
                  {Array.isArray(p.ineligible_expenses) && p.ineligible_expenses.length > 0 ? (
                    <p className="text-sm print:text-[8pt] text-muted-foreground leading-relaxed print:leading-snug">
                      {p.ineligible_expenses.join(' · ')}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground/70">—</p>
                  )}
                </RequirementCard>

                <RequirementCard title="Entry Requirements">
                  {p.consortium_requirements || p.entry_requirements ? (
                    <>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1 text-xs print:text-[7pt]">
                        {p.consortium_requirements && (
                          <EntryFlag
                            value={p.consortium_requirements.required}
                            label={p.consortium_requirements.required
                              ? `Consortium (${p.consortium_requirements.min_entities ?? '?'} entities / ${p.consortium_requirements.min_countries ?? '?'} countries)`
                              : 'No consortium required'}
                          />
                        )}
                        {p.entry_requirements?.eu_entity != null && (
                          <EntryFlag value={p.entry_requirements.eu_entity} label="EU entity required" />
                        )}
                        {p.entry_requirements?.us_entity != null && (
                          <EntryFlag value={p.entry_requirements.us_entity} label="US entity required" />
                        )}
                        {p.entry_requirements?.sam_gov != null && (
                          <EntryFlag value={p.entry_requirements.sam_gov} label="SAM.gov / UEI" />
                        )}
                        {p.entry_requirements?.ownership_control != null && (
                          <EntryFlag
                            value={p.entry_requirements.ownership_control}
                            label="Ownership control check"
                          />
                        )}
                        {p.entry_requirements?.security_clearance != null && (
                          <EntryFlag
                            value={p.entry_requirements.security_clearance}
                            label="Security clearance"
                          />
                        )}
                        {p.entry_requirements?.trl_min != null && (
                          <span className="flex items-center gap-1.5 text-muted-foreground">
                            <span className="w-3.5 h-3.5 rounded-full bg-primary/10 flex items-center justify-center text-[9px] font-bold text-primary shrink-0">
                              {p.entry_requirements.trl_min}
                            </span>
                            Min TRL
                          </span>
                        )}
                      </div>
                      {p.entry_requirements?.details && (
                        <p className="text-xs text-muted-foreground mt-2 print:text-[7pt] leading-snug">
                          {p.entry_requirements.details}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-muted-foreground/70">—</p>
                  )}
                </RequirementCard>
              </div>
            </div>

            <Separator className="my-4 print:my-3" />

            {/* Description: collapsed toggle on screen, always shown in print */}
            <div className="program-card__desc">
              <button
                type="button"
                onClick={() => setDescriptionOpen((v) => !v)}
                className="print:hidden flex w-full items-center gap-2 rounded-md py-1 -mx-1 px-1 text-left hover:bg-muted/50 transition-colors"
                aria-expanded={descriptionOpen}
                aria-controls={`program-desc-${p.id}`}
              >
                <ChevronDown
                  className={cn(
                    'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
                    descriptionOpen && 'rotate-180',
                  )}
                  aria-hidden
                />
                <span className={sectionLabelClass}>
                  Program Description
                </span>
              </button>
              {/* Screen: conditional render based on toggle */}
              {descriptionOpen && (
                <p
                  id={`program-desc-${p.id}`}
                  className="print:hidden text-sm text-muted-foreground leading-relaxed mt-2"
                >
                  {p.description}
                </p>
              )}
              {/* Print: always rendered */}
              <p className={cn(sectionLabelClass, 'hidden print:block print:mb-1')}>
                Program Description
              </p>
              <p className="hidden print:block text-sm print:text-[9pt] text-muted-foreground leading-relaxed print:leading-snug">
                {p.description}
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
